import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AuthService } from "../services/auth.service";
import { MainLayout } from "./MainLayout";
import { Card } from "./ui/card";
import { UserModel } from "../models/user.model";
import { DataService } from "../services/data.service";
import { getCurrentDate, getCo2Status } from "../lib/utils";
import { ActivityReport } from "./ActivityReport";
import { ChartCard, RoomOption } from "./ChartCard";
import { MapPinHouse, Thermometer, Droplets, WindArrowDown, Lightbulb, Wind, Radar, ArrowLeft, Smile, History, ChartArea, BedDouble, Toilet, ThermometerSnowflake, Clock, Bath, Utensils, Tv, TreeDeciduous } from "lucide-react";
import { Beneficiary } from "./Beneficiary";
import { LocationReport } from "./LocationReport";
import { WalksReport } from "./WalksReport";
import { DeviceHealthBanner } from "./DeviceHealthBanner";
import { BloodPressureReport } from "./BloodPressureReport";
import { WatchHealthReport } from "./WatchHealthReport";
import { SleepSoundReport } from "./SleepSoundReport";
import { LocateNow } from "./LocateNow";
import { useLogout } from "../hooks/logout.hook";
import { PageTitle } from "./PageTitle";
import { SectionNav } from "./SectionNav";
import { DatePicker } from "./DatePicker";

// Date dropdowns cascade DOWN the page: changing a report's date snaps all
// reports rendered after it to the same offset, while reports above stay
// independent. Order here is document order — keep it in sync with the JSX
// below or the cascade will skip / over-cascade.
type ReportKey = 'location' | 'sleep' | 'walks'
const REPORT_ORDER: ReportKey[] = ['location', 'sleep', 'walks']

export function DashboardDetails() {
    const navigate = useNavigate()
    const logout = useLogout()
    const { id } = useParams<{ id: any }>()
    const [user, setUser] = useState<UserModel>()
    const [userForDate, setUserForDate] = useState<UserModel>()
    // Radar time-in-bed for a PAST Sleep Report date. dashboard_single ignores
    // `date` (backend bf7c1ef), so userForDate.sleep_hours is always today's;
    // past dates read /api/legacy/sleep-summary instead. undefined = loading or
    // today (use userForDate), null = no night recorded.
    const [pastSleepHours, setPastSleepHours] = useState<number | null | undefined>(undefined)
    const [reportDates, setReportDates] = useState<Record<ReportKey, number>>({ location: 0, sleep: 0, walks: 0 })
    // Bumped on every silent 15-min refresh. The BloodPressure / WatchHealth
    // panels key on stable numeric ids (deployment_id / person_user_id), so the
    // user-object refetch alone never re-runs their effects — passing this tick
    // as a prop is what makes them pick up fresh readings on the page cadence.
    const [refreshTick, setRefreshTick] = useState(0)
    // Room options for the sensor cards: prefer the well_id-bearing `rooms[]`
    // (well-api v537+) so queries key on the unique device id; fall back to the
    // legacy `location_list` label strings when the API doesn't send `rooms`.
    const roomOptions: RoomOption[] = user?.rooms?.length
        ? user.rooms.map(r => ({ well_id: r.well_id, label: r.label }))
        : (user?.location_list ?? []).map(label => ({ label }))

    useEffect(() => {
        if (!AuthService.hasAuth()) {
            navigate('/login', { replace: true });
            return
        }

        loadData()
        const interval = setInterval(() => loadData(true), 15 * 60 * 1000)
        return () => clearInterval(interval)
    }, [])

    async function loadData(silent = false) {
        try {
            const userRsp = await DataService.getUserById(id, silent)
            setUser(userRsp)

            // Initialise the per-date snapshot only on the first load, so
            // changing dropdowns doesn't reset to today on the 15-min refresh.
            if (!userForDate) setUserForDate(userRsp)

            // Only the silent (interval) refreshes drive the id-keyed panels.
            // On the initial load they already fetch on mount, so bumping here
            // would just double-fetch — guard on `silent`.
            if (silent) setRefreshTick(t => t + 1)
        } catch (e: any) {
            logout()
        }
    }

    // Refetch the date-scoped user snapshot whenever Sleep's date changes —
    // Sleep reads sleep_hours / bathroom_visit_count / bedroom_co2 for the
    // selected day. Location and Walks fetch their own data for their own
    // offset, so they don't need this snapshot.
    useEffect(() => {
        if (!user) return
        setPastSleepHours(undefined)
        if (reportDates.sleep === 0) {
            setUserForDate(user)
            return
        }
        let cancelled = false
        const date = getCurrentDate(reportDates.sleep, user.time_zone)
        DataService.getUserById(id, true, date)
            .then(rsp => setUserForDate(rsp))
            .catch(() => logout())
        DataService.getRadarSleepNight(user.deployment_id, date)
            .then(n => { if (!cancelled) setPastSleepHours(n?.time_in_bed_h ?? null) })
        return () => { cancelled = true }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reportDates.sleep])

    function backToDashboard() {
        navigate('/dashboard', { replace: true });
    }

    function goToDetailedReport(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
        e.preventDefault()
        const element = document.getElementById('activity_report');
        if (element) {
            element.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    }

    const cascadeChangeDate = (from: ReportKey) => (v: number) => {
        const idx = REPORT_ORDER.indexOf(from)
        setReportDates(prev => {
            const next = { ...prev }
            for (let i = idx; i < REPORT_ORDER.length; i++) next[REPORT_ORDER[i]] = v
            return next
        })
    }

    return (
        <MainLayout>
            <PageTitle>
                <ArrowLeft className="text-3xl text-gray-800 cursor-pointer" onClick={backToDashboard} size={32} />
                <h1 className="text-2xl font-bold text-gray-800 cursor-pointer" onClick={backToDashboard}>Dashboard Details</h1>
            </PageTitle>


            {user &&
                <div className="space-y-2">
                    <SectionNav />

                    <Beneficiary user={user} />

                    <DeviceHealthBanner beneficiaryUserId={user.person_user_id} />


                    {user.settings.wellness_secore &&
                        <Card className="p-4 space-y-4 shadow-md rounded-lg border" id="score">
                            <div className="space-y-2">
                                <h3 className="text-lg font-semibold text-gray-800 flex items-center border-b pb-4">
                                    <Smile className="mr-2 text-gray-600 bg-gray-100 p-2 rounded-lg text-3xl" size={32} /> Wellness Score
                                </h3>
                                <p className="text-sm text-gray-500">
                                    Condition level: <span className={user.wellness_descriptor_color}>{user.wellness_descriptor}</span>
                                </p>
                                <div className="h-2 bg-gray-200 rounded-full">
                                    <div className="h-2 bg-green-500 rounded-full" style={{ width: `${user.wellness_score_percent}%` }}></div>
                                </div>
                                <div className="flex justify-center">
                                    <button className="w-2/4 py-2 text-sm font-medium text-white bg-blue-500 border border-blue-500 rounded-lg hover:bg-blue-600" type="button" onClick={e => goToDetailedReport(e)}>
                                        View detailed report
                                    </button>
                                </div>
                            </div>
                        </Card>
                    }

                    {user.settings.last_seen &&
                        <Card className="p-4 space-y-4 shadow-md rounded-lg border" id="room">
                            <div className="flex justify-between items-center border-b pb-4">
                                <div className="space-y-2">
                                    <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                                        <History className="mr-2 text-gray-600 bg-gray-100 p-2 rounded-lg text-3xl" size={32} /> Last Seen
                                    </h3>
                                </div>
                                <div className={`${user.last_seen_alert_colors} text-sm font-medium px-4 py-1 rounded-lg`}>
                                    {user.last_seen_alert}
                                </div>
                            </div>

                            <div className="space-y-2">
                                <div className="flex items-center bg-gray-100 p-2 rounded-lg space-x-3">
                                    <div className="bg-gray-100 p-2 rounded-lg">
                                        <MapPinHouse className="text-gray-800" />
                                    </div>

                                    <div className="flex flex-col">
                                        <p className="text-sm font-medium text-gray-800">{user.most_time_spent_in}</p>
                                        <span className="text-xs text-gray-500">Most time spent</span>
                                    </div>
                                </div>
                            </div>
                        </Card>
                    }

                    <LocationReport
                        id={user.deployment_id}
                        globalDateOffset={reportDates.location}
                        changeDate={cascadeChangeDate('location')}
                        timezone={user.time_zone}
                    />

                    <LocateNow
                        beneficiaryUserId={user.person_user_id}
                        timezone={user.time_zone}
                    />

                    {user.settings.sleep_report && userForDate &&
                        <Card className="p-4 space-y-4 shadow-md rounded-lg border" id="sleep_report">
                            <div className="space-y-2">
                                <div className="flex justify-between items-center border-b pb-2">
                                    <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                                        <ChartArea className="mr-2 text-gray-600 bg-gray-100 p-2 rounded-lg text-3xl" size={32} /> In Bed
                                    </h3>
                                    <DatePicker value={reportDates.sleep} onChange={cascadeChangeDate('sleep')} timezone={user.time_zone} />
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div className="flex flex-col p-2 bg-gray-100 rounded-lg">
                                        <div className="flex justify-between items-center">
                                            <p className="text-sm font-medium text-gray-800">
                                                {reportDates.sleep === 0
                                                    ? (userForDate.sleep_hours == null || userForDate.sleep_hours === '' ? 'No data' : `${userForDate.sleep_hours} H`)
                                                    : pastSleepHours === undefined ? '…'
                                                    : pastSleepHours === null ? 'No data'
                                                    : `${pastSleepHours} H`}
                                            </p>
                                            <BedDouble className="text-gray-800" />
                                        </div>
                                        <span className="text-xs text-gray-500">Hours in bed</span>
                                    </div>
                                    <div className="flex flex-col p-2 bg-gray-100 rounded-lg">
                                        <div className="flex justify-between items-center">
                                            <p className="text-sm font-medium text-gray-800">{userForDate.sleep_bathroom_visit_count} times</p>
                                            <Toilet className="text-gray-800" />
                                        </div>
                                        <span className="text-xs text-gray-500">Bathroom Visits</span>
                                    </div>
                                    <div className="flex flex-col p-2 bg-gray-100 rounded-lg">
                                        <div className="flex justify-between items-center">
                                            <p className="text-sm font-medium text-gray-800">{Math.round(userForDate.bedroom_temperature)} {userForDate.units}</p>
                                            <ThermometerSnowflake className="text-gray-800" />
                                        </div>
                                        <span className="text-xs text-gray-500">Bedroom Temperature</span>
                                    </div>
                                    <div className="flex flex-col p-2 bg-gray-100 rounded-lg">
                                        <div className="flex justify-between items-center">
                                            <p className={`text-sm font-medium ${userForDate.bedroom_co2 > 0 ? getCo2Status(userForDate.bedroom_co2).textClass : 'text-gray-800'}`}>{userForDate.bedroom_co2} PPM</p>
                                            <Wind className="text-gray-800" />
                                        </div>
                                        <span className="text-xs text-gray-500">Room CO2{userForDate.bedroom_co2 > 0 ? ` · ${getCo2Status(userForDate.bedroom_co2).label}` : ''}</span>
                                    </div>
                                </div>
                            </div>
                        </Card>
                    }

                    {/* Self-hides (renders null) unless the account has blood-pressure
                        readings — visibility is data-driven, no settings flag. */}
                    <BloodPressureReport deploymentId={user.deployment_id} timezone={user.time_zone} refreshKey={refreshTick} />

                    {/* Self-hides unless the account has watch Health Services data — visibility
                        is data-driven, keyed on person_user_id, no settings flag (matches
                        BloodPressureReport). Its own 7/30/90 toggle, not in REPORT_ORDER. */}
                    <WatchHealthReport beneficiaryUserId={user.person_user_id} timezone={user.time_zone} refreshKey={refreshTick} />

                    {/* Self-hides unless the phone (or later a WellPlug) has recorded
                        snoring nights — data-driven, keyed on person_user_id, like
                        WatchHealthReport. Its own 7/30/90 toggle, not in REPORT_ORDER. */}
                    <SleepSoundReport beneficiaryUserId={user.person_user_id} timezone={user.time_zone} refreshKey={refreshTick} />

                    <WalksReport
                        beneficiaryUserId={user.person_user_id}
                        timezone={user.time_zone}
                        dateOffset={reportDates.walks}
                        onDateChange={cascadeChangeDate('walks')}
                    />

                    {user.settings.activity_report && <ActivityReport deploymentId={user.deployment_id} />}

                    {user.settings.radar &&
                        <ChartCard deploymentId={user.deployment_id} sensor={'radar'} timezone={user.time_zone} rooms={roomOptions} lastLocation={user.last_location}>
                            <Radar className="mr-2 text-gray-600 bg-gray-100 p-2 rounded-lg" size={32} /> Radar Data
                        </ChartCard>
                    }

                    {user.settings.light &&
                        <ChartCard deploymentId={user.deployment_id} sensor={'light'} timezone={user.time_zone} rooms={roomOptions} lastLocation={user.last_location}>
                            <Lightbulb className="mr-2 text-gray-600 bg-gray-100 p-2 rounded-lg" size={32} /> Room Light
                        </ChartCard>
                    }

                    {user.settings.air_quality &&
                        <ChartCard deploymentId={user.deployment_id} sensor={'co2'} timezone={user.time_zone} rooms={roomOptions} lastLocation={user.last_location}>
                            <Wind className="mr-2 text-gray-600 bg-gray-100 p-2 rounded-lg" size={32} /> Air Quality (CO<sub>2</sub>)
                        </ChartCard>
                    }

                    {user.settings.temperature &&
                        <ChartCard deploymentId={user.deployment_id} sensor={'temperature'} timezone={user.time_zone} rooms={roomOptions} lastLocation={user.last_location}>
                            <Thermometer className="mr-2 text-gray-600 bg-gray-100 p-2 rounded-lg" size={32} /> Temperature
                        </ChartCard>
                    }

                    {user.settings.humidity &&
                        <ChartCard deploymentId={user.deployment_id} sensor={'humidity'} timezone={user.time_zone} rooms={roomOptions} lastLocation={user.last_location}>
                            <Droplets className="mr-2 text-gray-600 bg-gray-100 p-2 rounded-lg" size={32} /> Humidity
                        </ChartCard>
                    }

                    {user.settings.air_pressure &&
                        <ChartCard deploymentId={user.deployment_id} sensor={'pressure'} timezone={user.time_zone} rooms={roomOptions} lastLocation={user.last_location}>
                            <WindArrowDown className="mr-2 text-gray-600 bg-gray-100 p-2 rounded-lg" size={32} /> Air Pressure
                        </ChartCard>
                    }

                    {user.settings.other_activities &&
                        <Card className="p-4 space-y-4 shadow-md rounded-lg border" id="other_activities">
                            <div className="space-y-2">
                                <h3 className="text-lg font-semibold text-gray-800 flex items-center border-b pb-4">
                                    <Clock className="mr-2 text-gray-600 bg-gray-100 p-2 rounded-lg text-3xl" size={32} />
                                </h3>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="flex flex-col p-2 bg-gray-100 rounded-lg">
                                        <div className="flex justify-between items-center">
                                            <p className="text-sm font-medium text-gray-800">{new Date(user.bathroom_at).toLocaleTimeString()}</p>
                                            <Bath className="text-gray-800" />
                                        </div>
                                        <span className="text-xs text-gray-500">Last Bathroom Visit</span>
                                    </div>
                                    <div className="flex flex-col p-2 bg-gray-100 rounded-lg">
                                        <div className="flex justify-between items-center">
                                            <p className="text-sm font-medium text-gray-800">{new Date(user.breakfast_detected_time).toLocaleTimeString()}</p>
                                            <Utensils className="text-gray-800" />
                                        </div>
                                        <span className="text-xs text-gray-500">Last Meal</span>
                                    </div>
                                    <div className="flex flex-col p-2 bg-gray-100 rounded-lg">
                                        <div className="flex justify-between items-center">
                                            <p className="text-sm font-medium text-gray-800">{user.living_room_time_spent} hours</p>
                                            <Tv className="text-gray-800" />
                                        </div>
                                        <span className="text-xs text-gray-500">Relaxing</span>
                                    </div>
                                    <div className="flex flex-col p-2 bg-gray-100 rounded-lg">
                                        <div className="flex justify-between items-center">
                                            <p className="text-sm font-medium text-gray-800">{user.outside_hours} hours</p>
                                            <TreeDeciduous className="text-gray-800" />
                                        </div>
                                        <span className="text-xs text-gray-500">Outside or Not Detected</span>
                                    </div>
                                </div>
                            </div>
                        </Card>
                    }
                </div>
            }
        </MainLayout>
    );
}